export class ExpGolomb {
  private _data: Uint8Array

  private _bytesAvailable: number

  private _bitsAvailable = 0

  private _word = 0

  constructor(data: Uint8Array) {
    if (!data) throw new Error('ExpGolomb data params is required')
    this._data = data
    this._bytesAvailable = data.byteLength
    if (this._bytesAvailable) this._loadWord()
  }

  get bitsAvailable(): number {
    return this._bitsAvailable
  }

  bitsPos(): number {
    return this._data.byteLength * 8 - this.bitsLeft()
  }

  bitsLeft(): number {
    return this._bytesAvailable * 8 + this._bitsAvailable
  }

  byteAligned(): boolean {
    return this.bitsPos() === 0 || this.bitsPos() % 8 === 0
  }

  private _loadWord(): void {
    const position = this._data.byteLength - this._bytesAvailable
    const availableBytes = Math.min(4, this._bytesAvailable)
    if (availableBytes === 0) throw new Error('No bytes available')

    const workingBytes = new Uint8Array(4)
    workingBytes.set(this._data.subarray(position, position + availableBytes))

    this._word = new DataView(workingBytes.buffer).getUint32(0)
    this._bitsAvailable = availableBytes * 8
    this._bytesAvailable -= availableBytes
  }

  skipBits(count: number): void {
    if (count < 0 || count > this.bitsLeft()) {
      throw new Error('Cannot skip beyond available bits')
    }
    if (this._bitsAvailable > count) {
      this._word <<= count
      this._bitsAvailable -= count
    } else {
      count -= this._bitsAvailable
      const skipBytes = Math.floor(count / 8)
      count -= skipBytes * 8
      this._bytesAvailable -= skipBytes
      if (!this._bytesAvailable) {
        this._bitsAvailable = 0
        return
      }
      this._loadWord()
      this._word <<= count
      this._bitsAvailable -= count
    }
  }

  readBits(size: number): number {
    if (size > 32) {
      throw new Error('Cannot read more than 32 bits')
    }
    if (size < 0 || size > this.bitsLeft()) {
      throw new Error('Cannot read beyond available bits')
    }
    if (size === 0) return 0

    let bits = Math.min(this._bitsAvailable, size)
    const val = this._word >>> (32 - bits)

    this._bitsAvailable -= bits
    if (this._bitsAvailable > 0) {
      this._word <<= bits
    } else if (this._bytesAvailable > 0) {
      this._loadWord()
    }

    bits = size - bits
    if (bits > 0 && this._bitsAvailable) {
      return (val << bits) | this.readBits(bits)
    }
    return val
  }

  skipLZ(): number {
    let leadingZeroCount
    for (
      leadingZeroCount = 0;
      leadingZeroCount < this._bitsAvailable;
      ++leadingZeroCount
    ) {
      if ((this._word & (0x80000000 >>> leadingZeroCount)) !== 0) {
        this._word <<= leadingZeroCount
        this._bitsAvailable -= leadingZeroCount
        return leadingZeroCount
      }
    }
    this._loadWord()
    return leadingZeroCount + this.skipLZ()
  }

  skipUEG(): void {
    this.skipBits(1 + this.skipLZ())
  }

  readUEG(): number {
    const clz = this.skipLZ()
    return this.readBits(clz + 1) - 1
  }

  readEG(): number {
    const val = this.readUEG()
    if (1 & val) {
      return (1 + val) >>> 1
    }
    return -1 * (val >>> 1)
  }

  readBool(): boolean {
    return this.readBits(1) === 1
  }

  readUByte(): number {
    return this.readBits(8)
  }

  skipScalingList(count: number): void {
    let lastScale = 8
    let nextScale = 8
    let deltaScale: number
    for (let j = 0; j < count; j++) {
      if (nextScale !== 0) {
        deltaScale = this.readEG()
        nextScale = (lastScale + deltaScale + 256) % 256
      }
      lastScale = nextScale === 0 ? lastScale : nextScale
    }
  }
}
